import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import {
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Flag,
  RefreshCw
} from 'lucide-react';
import { api } from '../services/api';
import type { Prayer } from '../services/api';

interface AdminStats {
  total: {
    pending: number;
    approved: number;
    rejected: number;
    flagged: number;
  };
}

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState<string>('pending');
  const [prayers, setPrayers] = useState<Prayer[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [stats, setStats] = useState<AdminStats['total']>({
    pending: 0,
    approved: 0,
    rejected: 0,
    flagged: 0
  });

  const fetchPrayers = async (status: string) => {
    setLoading(true);
    try {
      const response = await api.admin.getPrayers({ status });
      setPrayers(response.data.prayers);
    } catch (error) {
      console.error('Error fetching prayers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.admin.getStats();
      setStats(response.data.total);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleModeration = async (prayerId: string, decision: 'approved' | 'rejected') => {
    try {
      await api.admin.moderatePrayer(prayerId, decision);
      fetchPrayers(activeTab);
      fetchStats();
    } catch (error) {
      console.error('Error moderating prayer:', error);
    }
  };

  useEffect(() => {
    fetchPrayers(activeTab);
    fetchStats();
  }, [activeTab]);

  const getStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
      pending: 'bg-[#2D2D39] text-[#FF8A4C]',
      approved: 'bg-green-900/20 text-green-300',
      rejected: 'bg-red-900/20 text-red-300',
      flagged: 'bg-orange-900/20 text-orange-300'
    };
    return colors[status] || 'bg-gray-900/20 text-gray-300';
  };

  const StatusBadge: React.FC<{ status: string; reportCount: number }> = ({ status, reportCount }) => (
    <div className="flex items-center space-x-2">
      <Badge className={getStatusColor(status)}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
      {reportCount > 0 && (
        <Badge className="bg-red-900/20 text-red-300">
          <Flag className="w-3 h-3 mr-1" />
          {reportCount}
        </Badge>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#171720] text-white p-6">
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-white">Prayer Moderation</h1>
          <Button 
            onClick={() => fetchPrayers(activeTab)}
            variant="outline"
            className="border-[#2D2D39] hover:bg-[#2D2D39] text-white"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        {Object.entries(stats).map(([key, value]) => (
          <Card key={key} className="bg-[#23232F] border-[#2D2D39]">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg capitalize flex items-center space-x-2">
                {key === 'pending' && <Clock className="w-4 h-4 text-[#FF8A4C]" />}
                {key === 'approved' && <CheckCircle className="w-4 h-4 text-green-500" />}
                {key === 'rejected' && <XCircle className="w-4 h-4 text-red-500" />}
                {key === 'flagged' && <Flag className="w-4 h-4 text-orange-500" />}
                <span>{key}</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{value.toString()}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="max-w-7xl mx-auto">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-4 gap-4 bg-transparent">
            {['pending', 'flagged', 'approved', 'rejected'].map((tab) => (
              <TabsTrigger
                key={tab}
                value={tab}
                className="data-[state=active]:bg-[#23232F] data-[state=active]:text-white rounded-xl capitalize border border-[#2D2D39]"
              >
                {tab}
              </TabsTrigger>
            ))}
          </TabsList>

          {['pending', 'flagged', 'approved', 'rejected'].map((status) => (
            <TabsContent key={status} value={status}>
              <div className="space-y-4">
                {loading ? (
                  <div className="text-center py-8">
                    <RefreshCw className="w-8 h-8 animate-spin mx-auto text-[#FF8A4C]" />
                  </div>
                ) : prayers.length === 0 ? (
                  <Card className="bg-[#23232F] border-[#2D2D39]">
                    <CardContent className="py-8 text-center text-gray-400">
                      No prayers to review
                    </CardContent>
                  </Card>
                ) : (
                  prayers.map((prayer) => (
                    <Card 
                      key={prayer._id}
                      className="bg-[#23232F] border-[#2D2D39] hover:border-[#FF8A4C]/50 transition-colors"
                    >
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <p className="text-sm text-gray-400">
                              From: {prayer.isAnonymous ? 'Anonymous' : prayer.senderName}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(prayer.createdAt).toLocaleString()}
                            </p>
                          </div>
                          <StatusBadge status={prayer.status} reportCount={prayer.reportCount} />
                        </div>

                        <p className="text-gray-200 mb-4">{prayer.content}</p>

                        {prayer.reportCount > 0 && (
                          <div className="mb-4 p-4 bg-red-900/20 rounded-xl border border-red-900/50">
                            <div className="flex items-center space-x-2 text-red-300">
                              <AlertTriangle className="w-4 h-4" />
                              <p className="font-medium">Reported {prayer.reportCount} times</p>
                            </div>
                          </div>
                        )}

                        <div className="flex justify-end space-x-3">
                          <Button
                            variant="outline"
                            onClick={() => handleModeration(prayer._id, 'rejected')}
                            className="border-[#2D2D39] hover:bg-[#2D2D39] text-white"
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Reject
                          </Button>
                          <Button
                            onClick={() => handleModeration(prayer._id, 'approved')}
                            className="bg-[#FF8A4C] hover:bg-[#FF7A4C] border-0"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Approve
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;